import { Link } from "react-router-dom";
import { Leaf, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { spotlight } from "./Reveal";

export default function ProductCard({ product, light = false }) {
    const { addItem, setDrawerOpen } = useCart();
    const img = product.images?.[0]?.src;
    const isVariable = product.type === "variable" && (product.variations?.length || 0) > 0;
    const outOfStock = !product.is_in_stock && product.type !== "variable";

    const tag = product.brand || product.strain;

    const quickAdd = () => {
        if (isVariable) return;
        addItem(product);
        setDrawerOpen(true);
    };

    const frame = light ? "border-ink/10 bg-white" : "border-leaf/12 bg-moss/50";
    const nameColor = light ? "text-ink" : "text-bone";
    const subColor = light ? "text-ink/50" : "text-sage";

    return (
        <div
            className={`group relative flex flex-col rounded-2xl border p-3 transition-all duration-500 card-spotlight ${frame} hover:border-leaf/40 hover:shadow-[0_20px_60px_-20px_rgba(76,175,80,0.35)]`}
            onMouseMove={spotlight}
            data-testid="product-card"
        >
            <Link to={`/product/${product.slug}`} data-testid={`product-link-${product.slug}`} className="block">
                <div className="relative aspect-square overflow-hidden rounded-xl bg-moss">
                    {img ? (
                        <img
                            src={img}
                            alt={product.images[0].alt || product.name}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                        />
                    ) : (
                        <div className="flex h-full w-full items-center justify-center text-leaf/40">
                            <Leaf size={36} />
                        </div>
                    )}
                    {product.on_sale && product.discount_percent > 0 && (
                        <span
                            className="absolute left-3 top-3 rounded-full bg-gold px-3 py-1 font-mono text-[11px] font-bold text-ink"
                            data-testid="product-sale-badge"
                        >
                            -{product.discount_percent}%
                        </span>
                    )}
                    {outOfStock && (
                        <div className="absolute inset-0 flex items-center justify-center bg-ink/70" data-testid="product-sold-out">
                            <span className="rounded-full border border-bone/40 px-4 py-2 font-mono text-xs uppercase tracking-widest text-bone">
                                Sold out
                            </span>
                        </div>
                    )}
                    {!isVariable && !outOfStock && (
                        <button
                            data-testid="product-quick-add"
                            onClick={(e) => {
                                e.preventDefault();
                                quickAdd();
                            }}
                            aria-label={`Add ${product.name} to bag`}
                            className="absolute bottom-3 right-3 flex h-11 w-11 items-center justify-center rounded-full bg-leaf text-ink shadow-lg transition-all duration-300 hover:bg-leaf-bright hover:scale-110 md:opacity-0 md:group-hover:opacity-100"
                        >
                            <Plus size={20} strokeWidth={2.6} />
                        </button>
                    )}
                    {isVariable && (
                        <span className="absolute bottom-3 right-3 rounded-full bg-ink/80 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-bone backdrop-blur">
                            {product.variations.length} options
                        </span>
                    )}
                </div>

                <div className="flex flex-1 flex-col px-1 pb-1 pt-3">
                    {tag && (
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-leaf-bright">{tag}</span>
                    )}
                    <h3 className={`mt-1 font-display text-sm md:text-base font-bold leading-snug line-clamp-2 ${nameColor}`}>
                        {product.name}
                    </h3>
                    <div className="mt-2 flex items-baseline gap-2">
                        <span className={`text-lg font-extrabold ${product.on_sale ? "text-leaf-bright" : light ? "text-ink" : "text-bone"}`} data-testid="product-price">
                            {product.price != null ? money2(product.price) : "—"}
                        </span>
                        {product.on_sale && product.regular_price > product.price && (
                            <span className={`text-xs line-through ${subColor}`}>{money2(product.regular_price)}</span>
                        )}
                    </div>
                </div>
            </Link>

            <button
                data-testid="product-quick-add"
                onClick={(e) => {
                    e.preventDefault();
                    quickAdd();
                }}
                disabled={outOfStock}
                aria-label={isVariable ? `Choose options for ${product.name}` : `Add ${product.name} to bag`}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-leaf py-3 text-sm font-bold text-ink transition-colors hover:bg-leaf-bright disabled:cursor-not-allowed disabled:opacity-40"
            >
                <Plus size={16} strokeWidth={2.6} />
                {outOfStock ? "Sold out" : isVariable ? "Choose options" : "Add to bag"}
            </button>
        </div>
    );
}

function money2(n) {
    return `$${Number(n || 0).toFixed(2)}`;
}
